import React, { FC, useEffect, useMemo, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { getEgonRoutesListFetch } from 'src/resources/egonRoutesList/reducer';
import { RootStoreType } from 'src/types/store';
import TableSimple from 'src/components/TableSimple';
import { ExportTypeType, TableRefType } from 'src/types/table';
import { allColumns } from './constants';
import TopBlock from './TopBlock';
import { useCheckPermission } from 'src/utils/checkPermission';
import ExtensionIcon from '@mui/icons-material/Extension';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import { getEgonDeliveryLabel } from 'src/utils/egonRoutes';

const EgonRoutesListContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>();

    const loading = useSelector((state: RootStoreType) => state.egonRoutesList.loading);
    const routes = useSelector((state: RootStoreType) => state.egonRoutesList.data);
    const shipperCodes = useSelector((state: RootStoreType) =>
        [...state.constantsCodes.data.shipperCodes].sort((a, b) => a.label.localeCompare(b.label))
    );
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const permissionCreate = useCheckPermission('EGON_ROUTE.CREATE');
    const permissionDetail = useCheckPermission('EGON_ROUTE.DETAIL');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('egonRoutes.title') }));
        dispatch(getEgonRoutesListFetch());
    }, []);

    const rows = useMemo(
        () =>
            (routes || []).map((route) => ({
                ...route,
                EGON_DELIVERY: route.EGON_DELIVERY_ID
                    ? getEgonDeliveryLabel({ id: route.EGON_DELIVERY_ID, name: route.EGON_DELIVERY_NAME })
                    : '',
            })),
        [routes]
    );

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('egonRoutes.action.exportFileName'));
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    return (
        <div data-testid="egon-routes-list">
            <TopBlock
                reloadList={() => dispatch(getEgonRoutesListFetch())}
                permissionCreate={permissionCreate}
                handleExport={handleExport}
            />
            <TableSimple
                tableRef={tableRef}
                rowIdKey="ID"
                rows={rows}
                loading={loading}
                columsLabelPrefix="egonRoutes.table"
                allColumns={allColumns}
                initSort={{ column: 'SHIPPER_CODE', direction: 'ASC' }}
                fullHeight
                heightVariant="top-tab-2"
                optionsSessionName="egonRoutesTable"
                userSettingsName="showEgonRoutesTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                selectOptions={{ shipperCodes, serviceCodes, countryOptionsCodes, boolNumberCodes }}
                rowActions={[
                    {
                        icon: <ExtensionIcon />,
                        label: translate('egonRoutes.action.detail'),
                        getLink: (row) => `/admin/egon-routes/${row.ID}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                ]}
            />
        </div>
    );
};

export default EgonRoutesListContainer;
